import React, { useRef, useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import './ESignatureCanvas.css';

interface ESignatureCanvasProps {
  dealId: string;
  sellerName: string;
  vehicleInfo: {
    year: number;
    make: string;
    model: string;
    price: number;
  };
  billOfSaleData: {
    depositElected: boolean;
    lodgementElected: boolean;
  };
  onSignatureComplete?: (signaturePath: string) => void;
}

export const ESignatureCanvas: React.FC<ESignatureCanvasProps> = ({
  dealId,
  sellerName,
  vehicleInfo,
  billOfSaleData,
  onSignatureComplete
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureDate, setSignatureDate] = useState(new Date().toISOString().split('T')[0]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    // White background
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Border
    ctx.strokeStyle = '#ddd';
    ctx.lineWidth = 1;
    ctx.strokeRect(0, 0, canvas.width, canvas.height);
  }, []);

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    setIsDrawing(true);
    draw(e);
  };

  const endDrawing = () => {
    setIsDrawing(false);
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDrawing && e.type !== 'mousedown' && e.type !== 'touchstart') {
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rect = canvas.getBoundingClientRect();
    let x: number, y: number;

    if ('touches' in e) {
      x = e.touches[0].clientX - rect.left;
      y = e.touches[0].clientY - rect.top;
    } else {
      x = e.clientX - rect.left;
      y = e.clientY - rect.top;
    }

    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#000';

    if (e.type === 'mousedown' || e.type === 'touchstart') {
      ctx.beginPath();
      ctx.moveTo(x, y);
    } else if (isDrawing) {
      ctx.lineTo(x, y);
      ctx.stroke();
    }
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.strokeStyle = '#ddd';
    ctx.lineWidth = 1;
    ctx.strokeRect(0, 0, canvas.width, canvas.height);

    setError(null);
  };

  const canvasToBlob = (): Promise<Blob> => {
    return new Promise((resolve, reject) => {
      const canvas = canvasRef.current;
      if (!canvas) {
        reject(new Error('Canvas not found'));
        return;
      }

      canvas.toBlob((blob) => {
        if (blob) {
          resolve(blob);
        } else {
          reject(new Error('Failed to convert canvas to blob'));
        }
      }, 'image/png');
    });
  };

  const handleSubmit = async () => {
    setError(null);
    setIsSubmitting(true);

    try {
      // Check if signature is empty
      const canvas = canvasRef.current;
      if (!canvas) {
        throw new Error('Canvas not found');
      }

      const ctx = canvas.getContext('2d');
      const imageData = ctx?.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData?.data;

      // Check if canvas has any non-white pixels
      if (data) {
        let hasSignature = false;
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i + 1];
          const b = data[i + 2];
          // If any pixel is not white (255, 255, 255), signature exists
          if (r !== 255 || g !== 255 || b !== 255) {
            hasSignature = true;
            break;
          }
        }

        if (!hasSignature) {
          throw new Error('Please draw a signature before submitting');
        }
      }

      // Convert canvas to PNG
      const signatureBlob = await canvasToBlob();
      const fileName = `signatures/${dealId}-${Date.now()}.png`;

      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('bill-of-sale')
        .upload(fileName, signatureBlob, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        throw new Error(`Upload failed: ${uploadError.message}`);
      }

      // Store in database
      const { error: dbError } = await supabase
        .from('bill_of_sale_documents')
        .insert([{
          deal_id: dealId,
          bos_signed_image_path: fileName,
          seller_signed_at: new Date().toISOString(),
          seller_signed_by_name: sellerName,
          both_signed: false
        }]);

      if (dbError) {
        throw new Error(`Database error: ${dbError.message}`);
      }

      // Update deal status to Contract Signed
      const { error: statusError } = await supabase
        .from('deals')
        .update({
          deal_status: 'contract_signed'
        })
        .eq('id', dealId);

      if (statusError) {
        throw new Error(`Status update failed: ${statusError.message}`);
      }

      setSuccess(true);
      if (onSignatureComplete) {
        onSignatureComplete(fileName);
      }

    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'An error occurred';
      setError(errorMsg);
      console.error('Signature submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="esignature-container">
        <div className="success-message">
          <div className="success-icon">✓</div>
          <h3>Vehicle Purchase Agreement Signed!</h3>
          <p>Thank you, {sellerName}. The signature has been received.</p>
          <p>The deal has advanced to Contract Signed.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="esignature-container">
      <div className="esignature-card">
        <h2>Vehicle Purchase Agreement - Sign Here</h2>

        <div className="esignature-vehicle-info">
          <div className="info-item">
            <strong>Seller Name:</strong> {sellerName}
          </div>
          <div className="info-item">
            <strong>Vehicle:</strong> {vehicleInfo.year} {vehicleInfo.make} {vehicleInfo.model}
          </div>
          <div className="info-item">
            <strong>Purchase Price:</strong> ${vehicleInfo.price?.toLocaleString('en-AU')}
          </div>
        </div>

        <div className="esignature-terms">
          <h3>Terms of Agreement</h3>
          <div className="terms-content">
            <p>I hereby acknowledge that I own the vehicle described above free and clear of all encumbrances and third-party interests, and I agree to sell it to Direct Auto on the terms shown above.</p>
            <div className="elections-box">
              <h4>Your Elections (Locked):</h4>
              <p>
                ☑ Holding Deposit: none — removed in v2.0-NL.2.0
              </p>
              <p>
                ☑ PPSR Lodgement: {billOfSaleData.lodgementElected ? 'Elected' : 'Not elected'}
              </p>
              <p className="terms-note">These elections are locked and cannot be changed.</p>
            </div>
          </div>
        </div>

        <div className="esignature-section">
          <label className="esignature-label">Your Signature:</label>
          <canvas
            ref={canvasRef}
            className="esignature-canvas"
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={endDrawing}
            onMouseLeave={endDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={endDrawing}
          />
          <p className="esignature-hint">Draw your signature in the box above</p>
          <button
            className="btn-clear-signature"
            onClick={clearSignature}
            disabled={isSubmitting}
          >
            Clear Signature
          </button>
        </div>

        <div className="esignature-date-section">
          <label htmlFor="signature-date">Date Signed:</label>
          <input
            id="signature-date"
            type="date"
            value={signatureDate}
            onChange={(e) => setSignatureDate(e.target.value)}
            className="date-input"
            disabled
          />
        </div>

        {error && (
          <div className="error-message">
            <strong>Error:</strong> {error}
          </div>
        )}

        <div className="esignature-actions">
          <button
            className="btn-submit-signature"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Signature'}
          </button>
          <p className="submission-note">
            By submitting, the seller confirms they have reviewed the terms and agree to sell this vehicle.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ESignatureCanvas;
